import { useMemo } from 'react';

import { useGetVideoVersionDownloads } from '@vimeo/gctl-api/hooks/useVideoVersionDownloads';
import type { VideoFile } from '@vimeo/gctl-api/models/components/schemas/video-file/VideoFile';

import { filterVideoFilesForKnownResolutionThenSortAlphabetically } from '../utils';
import { mapVideoIdTypeForGCTL } from '../utils/mapVideoIdTypeForGCTL';

type Props = {
  videoId?: string;
  versionId?: number;
  audiotrackUri?: string;
  disabled?: boolean;
};
export function useDownloadLinksForVersion({ videoId, versionId, disabled, audiotrackUri }: Props) {
  const {
    error: downloadsError,
    mutate: getDownloads,
    data: videoDownloadsResponse,
    isLoading: isDownloadsLoading,
    isValidating: isDownloadsValidating,
  } = useGetVideoVersionDownloads(
    () => {
      if (!videoId || !versionId || disabled) {
        return null;
      }
      const audiotrackId = audiotrackUri?.split('/').pop();
      return {
        where: {
          ...mapVideoIdTypeForGCTL(videoId),
          versionId,
        },
        headers: { Accept: 'application/vnd.vimeo.*+json;version=3.4.10' },
        query: {
          audiotrackId,
        },
      };
    },
    {
      keepPreviousData: true,
      refreshWhenHidden: false,
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  );

  const hasValidResponse =
    isObject(videoDownloadsResponse) && Array.isArray(videoDownloadsResponse.download);

  const downloadableFiles: VideoFile[] = useMemo(() => {
    if (hasValidResponse) {
      return filterVideoFilesForKnownResolutionThenSortAlphabetically(
        videoDownloadsResponse.download as VideoFile[],
        {
          allowDownloadOriginal: !audiotrackUri,
        },
      );
    }
    return [];
  }, [audiotrackUri, videoDownloadsResponse, hasValidResponse]);

  return {
    downloadableFiles,
    downloadsError,
    getDownloads,
    isDownloadsLoading,
    isDownloadsValidating,
    hasValidResponse,
  };
}
function isObject(obj: unknown): obj is { [key: string]: unknown } {
  return typeof obj === 'object' && obj !== null;
}
